import { FC, ReactNode, useRef } from "react";
import "./ProfileLayout.scss";
import { useAuth } from "../../providers/AuthProvider";
import { Link, useLocation } from "react-router-dom";
import { BvIcon } from "bevi-icon";
import { useMediaQuery } from "react-responsive";

const getInitials = (name?: string) => {
  if (!name) return "";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

interface ProfileLayoutProps {
  children: ReactNode;
  profileImageUrl?: string | null;
}

const NAV_ITEMS = [
  { label: "Geral", path: "/profile/general" },
  { label: "Conta", path: "/profile/account" },
  { label: "Privacidade", path: "/profile/privacy" },
  { label: "Uso", path: "/profile/use" },
  { label: "Dispositivos", path: "/profile/devices" },
];

const ProfileLayout: FC<ProfileLayoutProps> = ({ children, profileImageUrl }) => {
  const { userLogged } = useAuth();
  const location = useLocation();
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const navRef = useRef<HTMLDivElement>(null);

  const hasImage = !!profileImageUrl;

  const scrollNav = () => {
    navRef.current?.scrollBy({ left: 120, behavior: "smooth" });
  };

  const renderAvatarSm = () => {
    if (hasImage) {
      return (
        <img
          src={profileImageUrl!}
          alt="Foto de perfil"
          className="profile-avatar-sm object-fit-cover"
          width={36}
          height={36}
        />
      );
    }
    return (
      <div className="profile-avatar-sm profile-avatar-placeholder d-flex align-items-center justify-content-center">
        <span className="profile-initials">{getInitials(userLogged?.name)}</span>
      </div>
    );
  };

  return (
    <div className="ProfileLayout">
      <div className="profile-layout-inner">
        {/* Settings Sidebar */}
        <div className="profile-sidebar shadow-sm">
          <div className="profile-user-info p-3 d-flex align-items-center gap-2 div-picture">
            {renderAvatarSm()}
            <div className="overflow-hidden">
              <div className="fw-bold text-sm text-uppercase text-truncate name">
                {userLogged?.name ?? ""}
              </div>
              <div className="text-xs text-truncate partner-code">
                {userLogged?.partner_code ?? ""}
              </div>
            </div>
          </div>

          <div className="profile-nav-wrapper">
            <div className="profile-nav" ref={navRef}>
              {NAV_ITEMS.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <div key={item.path} className="d-flex align-items-center">
                    {!isMobile && (
                      <div className={`profile-nav-side ${isActive ? "active" : ""}`}></div>
                    )}
                    <Link
                      to={item.path}
                      className={`profile-nav-item ${isActive ? "active" : ""}`}
                    >
                      {item.label}
                    </Link>
                  </div>
                );
              })}
            </div>
            {isMobile && (
              <button className="profile-nav-scroll-btn" onClick={scrollNav}>
                <BvIcon name="arrowForward" variant="solid" weight={600} width={16} height={16} />
              </button>
            )}
          </div>
        </div>

        {/* Page Content */}
        <div className="profile-content-card shadow-sm">
          <div className="profile-content p-4">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileLayout;
